/**
 * Light and dark MUI theme definitions with CSS custom property mappings.
 *
 * Each theme defines MUI palette, typography, and component overrides.
 * CSS custom properties (--verisa-*) are derived from these definitions
 * and injected into :root by ThemeProvider for plugin isolation.
 *
 * @module theme/themes
 * @see TASK-FRONTEND-THEME-PROVIDER-CHG-002
 * @see SPEC-FRONTEND-RULE-008
 */
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { dialogTitleClasses } from "@mui/material/DialogTitle";
import { colors } from "./colors";
// The one contrast formula in the tree (the Brand card and
// internal/repository/branding_contrast.go mirror it) - the dark-surface gate
// below must not invent a second one.
import { getContrastRatio, meetsAA } from "../utils/wcag";

// ---------------------------------------------------------------------------
// CSS Custom Property prefix
// ---------------------------------------------------------------------------

export const CSS_VAR_PREFIX = "--verisa" as const;

// ---------------------------------------------------------------------------
// Custom property names (used both for generation and consumption)
// ---------------------------------------------------------------------------

export const CSS_VARS = {
  primary: `${CSS_VAR_PREFIX}-primary`,
  primaryLight: `${CSS_VAR_PREFIX}-primary-light`,
  primaryDark: `${CSS_VAR_PREFIX}-primary-dark`,
  secondary: `${CSS_VAR_PREFIX}-secondary`,
  secondaryLight: `${CSS_VAR_PREFIX}-secondary-light`,
  secondaryDark: `${CSS_VAR_PREFIX}-secondary-dark`,
  background: `${CSS_VAR_PREFIX}-background`,
  backgroundPaper: `${CSS_VAR_PREFIX}-background-paper`,
  textPrimary: `${CSS_VAR_PREFIX}-text-primary`,
  textSecondary: `${CSS_VAR_PREFIX}-text-secondary`,
  error: `${CSS_VAR_PREFIX}-error`,
  warning: `${CSS_VAR_PREFIX}-warning`,
  info: `${CSS_VAR_PREFIX}-info`,
  success: `${CSS_VAR_PREFIX}-success`,
  fontFamily: `${CSS_VAR_PREFIX}-font-family`,
  fontSize: `${CSS_VAR_PREFIX}-font-size`,
} as const;

// ---------------------------------------------------------------------------
// Type for workspace theme presets coming from the API
// ---------------------------------------------------------------------------

export interface WorkspaceThemePreset {
  primary?: string;
  primaryLight?: string;
  primaryDark?: string;
  secondary?: string;
  secondaryLight?: string;
  secondaryDark?: string;
  fontFamily?: string;
  fontSize?: number;
  logoUrl?: string;
  darkPalette?: {
    primary?: string;
    primaryLight?: string;
    primaryDark?: string;
    secondary?: string;
    background?: string;
    backgroundPaper?: string;
    textPrimary?: string;
    textSecondary?: string;
  };
}

// ---------------------------------------------------------------------------
// Base typography shared between modes
// ---------------------------------------------------------------------------

const baseTypography: ThemeOptions["typography"] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: 14,
  h1: { fontWeight: 700, fontSize: "2rem" },
  h2: { fontWeight: 600, fontSize: "1.5rem" },
  h3: { fontWeight: 600, fontSize: "1.25rem" },
  body1: { fontSize: "1rem", lineHeight: 1.6 },
  body2: { fontSize: "0.875rem", lineHeight: 1.5 },
};

// ---------------------------------------------------------------------------
// Shared component overrides
// ---------------------------------------------------------------------------

const baseComponents: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: 8,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
  },
  // The notch - the gap in an outlined field's border where the shrunk label
  // sits - is as wide as the fieldset's <legend>, whose font-size MUI sets to
  // 0.75em of the INPUT root. The label itself renders at 0.75 x body1. So any
  // wrapper that sets the input root's font-size (StyledSelect and
  // StyledAutocomplete set 14px) got a notch narrower than its label, and the
  // border ran through the end of the label (measured: "Felületek másolása
  // innen" 152 px of label in 133 px of notch). Tie the legend to the label's
  // own size so the two cannot disagree (SPEC-FRONTEND-RULE-047,
  // TASK-BUGFIX-MUI-FIELD-LABEL-NOTCH).
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: ({ theme }) => ({
        "& legend": {
          fontSize: `calc(${theme.typography.body1.fontSize} * 0.75)`,
        },
      }),
    },
  },
  // MUI zeroes the top padding of a DialogContent that follows a DialogTitle
  // (`.MuiDialogTitle-root + .MuiDialogContent-root`, two classes, so no
  // single-class `sx` such as `pt: 2` can undo it), while the content scrolls.
  // A first field's shrunk label sits 9 px above the field, so it was cut in
  // half. Keep room for it; the selector is MUI's own, applied later, so it
  // wins. The `dividers` variant keeps MUI's padding (SPEC-FRONTEND-RULE-047,
  // TASK-BUGFIX-MUI-FIELD-LABEL-NOTCH).
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme, ownerState }) =>
        ownerState.dividers
          ? {}
          : {
              [`.${dialogTitleClasses.root} + &`]: {
                paddingTop: theme.spacing(1.5),
              },
            },
    },
  },
  // MUI's filled Alert does NOT read palette.success.contrastText - it calls
  // palette.getContrastText(palette.success.main). Pin the pair here so EVERY
  // success notification renders identically with ToastProvider, which pins
  // its own colors, and passes WCAG AA: colors.status.success + white is
  // 5.48:1.
  MuiAlert: {
    styleOverrides: {
      filledSuccess: {
        backgroundColor: colors.status.success,
        color: "#fff",
        [`& .MuiAlert-icon`]: { color: "#fff" },
        [`& .MuiAlert-action .MuiIconButton-root`]: { color: "#fff" },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        '&[type="number"]': {
          MozAppearance: "textfield",
        },
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Light theme
// ---------------------------------------------------------------------------

export const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: "#9c27b0",
      light: "#ba68c8",
      dark: "#7b1fa2",
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    error: { main: colors.status.error },
    warning: { main: colors.status.warning },
    info: { main: colors.status.info },
    // Dark-green success - pin white text/icon so filled success Chips
    // (e.g. the "Completed" training-job badge + its tick) stay legible:
    // white on colors.status.success is 5.48:1.
    success: { main: colors.status.success, contrastText: "#fff" },
  },
  typography: {
    ...baseTypography,
    h1: { fontWeight: 600, fontSize: "2rem", color: colors.text.selected },
    h2: { fontWeight: 600, fontSize: "1.5rem", color: colors.text.selected },
    h3: { fontWeight: 600, fontSize: "1.25rem", color: colors.text.selected },
    h4: { fontWeight: 600, color: colors.text.selected },
    h5: { fontWeight: 600, color: colors.text.selected },
    h6: { fontWeight: 600, color: colors.text.selected },
  },
  components: baseComponents,
};

// ---------------------------------------------------------------------------
// Dark theme
// ---------------------------------------------------------------------------

export const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#90caf9",
      light: "#e3f2fd",
      dark: "#42a5f5",
    },
    secondary: {
      main: "#ce93d8",
      light: "#f3e5f5",
      dark: "#ab47bc",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#e0e0e0",
      secondary: "#a0a0a0",
    },
    error: { main: "#f44336" },
    warning: { main: "#ffa726" },
    info: { main: "#29b6f6" },
    success: { main: "#66bb6a", contrastText: "#fff" },
  },
  typography: baseTypography,
  components: baseComponents,
};

// ---------------------------------------------------------------------------
// Light-mode constant, or the palette's value in dark mode
// ---------------------------------------------------------------------------

/**
 * For shared wrappers styled with the light-only `colors.*` constants: light
 * mode keeps the constant (pixel-identical to before), dark mode draws the
 * palette's value, so nothing light-on-light or dark-on-dark lands on a dark
 * surface (WCAG 1.4.3 / 1.4.11 / 2.4.7).
 */
export function byPaletteMode(theme: Theme, light: string, dark: string): string {
  return theme.palette.mode === "dark" ? dark : light;
}

// ---------------------------------------------------------------------------
// Derive CSS custom properties from a ThemeOptions object
// ---------------------------------------------------------------------------

export function themeToCssVars(
  options: ThemeOptions,
): Record<string, string> {
  const palette = options.palette as Record<string, unknown>;
  const primary = palette.primary as Record<string, string> | undefined;
  const secondary = palette.secondary as Record<string, string> | undefined;
  const background = palette.background as Record<string, string> | undefined;
  const text = palette.text as Record<string, string> | undefined;
  const error = palette.error as Record<string, string> | undefined;
  const warning = palette.warning as Record<string, string> | undefined;
  const info = palette.info as Record<string, string> | undefined;
  const success = palette.success as Record<string, string> | undefined;
  const typo = options.typography as Record<string, unknown> | undefined;

  return {
    [CSS_VARS.primary]: primary?.main ?? "",
    [CSS_VARS.primaryLight]: primary?.light ?? "",
    [CSS_VARS.primaryDark]: primary?.dark ?? "",
    [CSS_VARS.secondary]: secondary?.main ?? "",
    [CSS_VARS.secondaryLight]: secondary?.light ?? "",
    [CSS_VARS.secondaryDark]: secondary?.dark ?? "",
    [CSS_VARS.background]: background?.default ?? "",
    [CSS_VARS.backgroundPaper]: background?.paper ?? "",
    [CSS_VARS.textPrimary]: text?.primary ?? "",
    [CSS_VARS.textSecondary]: text?.secondary ?? "",
    [CSS_VARS.error]: error?.main ?? "",
    [CSS_VARS.warning]: warning?.main ?? "",
    [CSS_VARS.info]: info?.main ?? "",
    [CSS_VARS.success]: success?.main ?? "",
    [CSS_VARS.fontFamily]: (typo?.fontFamily as string) ?? "",
    [CSS_VARS.fontSize]: typo?.fontSize ? `${typo.fontSize}px` : "",
  };
}

// ---------------------------------------------------------------------------
// Merge workspace presets into a ThemeOptions object
// ---------------------------------------------------------------------------

export function applyWorkspacePreset(
  base: ThemeOptions,
  preset: WorkspaceThemePreset,
): ThemeOptions {
  const mode = (base.palette as Record<string, unknown>)?.mode as string;
  const isDark = mode === "dark";
  const dp = isDark ? preset.darkPalette : undefined;

  return {
    ...base,
    palette: {
      ...base.palette,
      primary: {
        main: dp?.primary ?? preset.primary ?? (base.palette as Record<string, Record<string, string>>).primary?.main,
        light: dp?.primaryLight ?? preset.primaryLight ?? (base.palette as Record<string, Record<string, string>>).primary?.light,
        dark: dp?.primaryDark ?? preset.primaryDark ?? (base.palette as Record<string, Record<string, string>>).primary?.dark,
      },
      secondary: {
        main: dp?.secondary ?? preset.secondary ?? (base.palette as Record<string, Record<string, string>>).secondary?.main,
        light: preset.secondaryLight ?? (base.palette as Record<string, Record<string, string>>).secondary?.light,
        dark: preset.secondaryDark ?? (base.palette as Record<string, Record<string, string>>).secondary?.dark,
      },
      ...(isDark && dp
        ? {
            background: {
              default: dp.background ?? (base.palette as Record<string, Record<string, string>>).background?.default,
              paper: dp.backgroundPaper ?? (base.palette as Record<string, Record<string, string>>).background?.paper,
            },
            text: {
              primary: dp.textPrimary ?? (base.palette as Record<string, Record<string, string>>).text?.primary,
              secondary: dp.textSecondary ?? (base.palette as Record<string, Record<string, string>>).text?.secondary,
            },
          }
        : {}),
    },
    typography: {
      ...base.typography,
      ...(preset.fontFamily ? { fontFamily: preset.fontFamily } : {}),
      ...(preset.fontSize ? { fontSize: preset.fontSize } : {}),
    },
  };
}

// ---------------------------------------------------------------------------
// Apply the installation brand colours (SPEC-PLATFORM-BRANDING RULE-007)
// ---------------------------------------------------------------------------

/**
 * The installation brand colours as they reach the palette: LITERAL `#rrggbb`
 * values, never a `var(--...)` (SPEC-FRONTEND-RULE-081 as amended by
 * SPEC-PLATFORM-BRANDING-RULE-007, which names the provider prop as the third
 * palette-hex source). Either channel may be absent, meaning "keep the built-in
 * value for that channel".
 */
export interface BrandPalette {
  primary?: string;
  secondary?: string;
}

/** The only value shape a brand channel may carry into the palette. */
const BRAND_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Is this channel value safe to put in `theme.palette`?
 *
 * MUI evaluates colour maths on palette values at render time and
 * `@mui/system/colorManipulator` THROWS on anything that is not an
 * rgb/hsl/color string, so a malformed value is DROPPED here rather than
 * handed to `createTheme` (SPEC-FRONTEND-RULE-081). That is also what keeps a
 * `var(--verisa-primary)` out: it fails this test like any other non-hex value.
 */
function isBrandHex(value: string | undefined): value is string {
  return typeof value === "string" && BRAND_HEX.test(value);
}

/**
 * Apply the installation brand colours to a `ThemeOptions`.
 *
 * WHY THIS IS NOT `applyWorkspacePreset` WITH ANOTHER ARGUMENT. That function
 * carries the dark-mode `darkPalette` coalesce (`dp?.primary ?? preset.primary`
 * above) that SPEC-FRONTEND-RULE-084 governs and that
 * EPIC-DARK-MODE-DELIVERY - shelved under D-261 - owns. Widening it to carry the
 * brand would restart shelved work, so the brand gets its own small function
 * that sets `palette.primary.main` / `palette.secondary.main` and leaves every
 * other palette key, including the light/dark shades, untouched.
 *
 * THE LIGHT THEME TAKES THE STORED COLOUR UNCONDITIONALLY (D-BR-18, amending
 * SPEC-PLATFORM-BRANDING-RULE-013). The gateway already refused, with a 400 and
 * the measured ratio, any colour whose white-on-colour contrast is below 4.5:1
 * on `#FFFFFF` - the surface every shipped build paints - so a second light-side
 * gate here could only disagree with the server about a value the server
 * accepted.
 *
 * THE DARK THEME APPLIES SPEC-FRONTEND-RULE-084's GATE, PER CHANNEL. When the
 * base options are the dark ones, a brand channel overrides the base value only
 * if the colour reaches WCAG AA (4.5:1) against BOTH dark surfaces - the default
 * background and the paper, read off the base options themselves so this gate
 * cannot drift from the palette it measures against. A channel that fails keeps
 * its base dark value; the other channel is unaffected. The verdict is SILENT
 * here: the Brand card already showed the operator both ratios at save time and
 * renders the runtime verdict the BrandingProvider exposes (RULE-007 names a
 * console warning as the defect RULE-084 retired). No dark-mode preset gate is
 * built here and `applyWorkspacePreset` is untouched (D-261).
 *
 * @param base the mode's theme options (light or dark)
 * @param brand the resolved installation colours
 * @returns `base` itself when nothing applies, so the caller's memo does not churn
 * @see SPEC-PLATFORM-BRANDING-RULE-007 / RULE-013 (D-BR-18)
 * @see SPEC-FRONTEND-RULE-081 / RULE-084
 */
export function applyBrandPalette(
  base: ThemeOptions,
  brand: BrandPalette,
): ThemeOptions {
  const palette = (base.palette ?? {}) as Record<string, unknown>;
  const isDark = palette.mode === "dark";
  const channels = palette as Record<string, Record<string, string> | undefined>;

  const resolve = (value: string | undefined): string | undefined => {
    if (!isBrandHex(value)) return undefined;
    if (isDark && !passesDarkSurfaces(value, channels.background)) return undefined;
    return value;
  };

  const primary = resolve(brand.primary);
  const secondary = resolve(brand.secondary);

  if (!primary && !secondary) return base;

  return {
    ...base,
    palette: {
      ...base.palette,
      ...(primary ? { primary: { ...channels.primary, main: primary } } : {}),
      ...(secondary ? { secondary: { ...channels.secondary, main: secondary } } : {}),
    },
  };
}

/**
 * Does `color` reach WCAG AA against BOTH dark surfaces of `background`?
 *
 * The surfaces are read from the options being modified rather than hardcoded,
 * so the gate always measures against the palette that will actually paint. If
 * either surface is missing or is not a plain hex - so the ratio cannot be
 * measured - the answer is NO: on the dark theme an unmeasurable colour keeps
 * the base value instead of being applied unchecked (SPEC-FRONTEND-RULE-084 is a
 * gate, and an unanswered gate is closed).
 */
function passesDarkSurfaces(
  color: string,
  background: Record<string, string> | undefined,
): boolean {
  const surfaces = [background?.default, background?.paper];
  if (!surfaces.every(isBrandHex)) return false;
  return surfaces.every((surface) => meetsAA(getContrastRatio(color, surface as string)));
}
